import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { brands } from '../data/brands';
import { services } from '../data/services';
import { capitalizeWords } from '../utils/stringUtils';
import ServiceCenterHero from '../components/services/ServiceCenterHero';
import ServiceCenterDetails from '../components/services/ServiceCenterDetails';
import ServiceCenterSchema from '../components/seo/ServiceCenterSchema';
import { generateMetadata } from '../modules/seo/generateMetadata';
import ContactCTA from '../components/shared/ContactCTA';

export default function BrandServiceLocation() {
  const { brand: brandId, service: serviceId, state, city, area } = useParams();
  
  const brand = brands.find(b => b.id === brandId);
  const service = services.find(s => s.id === serviceId);

  // Only require brand, service, and state to be present
  if (!brand || !service || !state) {
    return <Navigate to="/brands" replace />;
  }

  const location = {
    state: capitalizeWords(state.replace(/-/g, ' ')),
    city: city ? capitalizeWords(city.replace(/-/g, ' ')) : undefined,
    area: area ? capitalizeWords(area.replace(/-/g, ' ')) : undefined
  };

  // Generate title based on location depth
  let title = `${brand.name} ${service.name} Service Center in ${location.state}`;
  if (location.city) {
    title = `${brand.name} ${service.name} Service Center in ${location.city}, ${location.state}`;
    if (location.area) {
      title = `${brand.name} ${service.name} Service Center in ${location.area}, ${location.city}`;
    }
  }

  const metadata = generateMetadata({
    service,
    city: location.city,
    state: location.state,
    brand: brand.name
  });

  const schema = {
    name: `${brand.name} ${service.name}`,
    description: `Professional ${brand.name} ${service.name.toLowerCase()} repair service center with expert technicians and same-day service.`,
    location: {
      city: location.city || location.state,
      state: location.state
    },
    services: [
      `${brand.name} ${service.name} Repair`,
      `${brand.name} ${service.name} Installation`,
      `${brand.name} ${service.name} Maintenance`
    ]
  };

  return (
    <>
      <Helmet>
        <title>{title} | Parakh Services</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords.join(', ')} />
      </Helmet>

      <ServiceCenterSchema {...schema} />

      <ServiceCenterHero 
        service={service}
        brandName={brand.name}
        location={location}
      />

      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ServiceCenterDetails 
            service={service}
            brandName={brand.name}
            location={location}
          />
        </div>
      </div>

      <ContactCTA />
    </>
  );
}